import { Page } from 'Core/models/Page';
import * as E from 'fp-ts/lib/Either';
import { Eq } from 'fp-ts/lib/Eq';
import { pipe } from 'fp-ts/lib/pipeable';

export class Empty {
  public readonly key = 'Empty';
}

export class Loading<T> {
  public readonly key = 'Loading';

  constructor(public readonly Data?: T) { }
}

export class Conflict {
  public readonly message = 'There is already a record with this identifier.';
  public readonly key = 'Conflict';
}

export class NotFound {
  public readonly message = 'Unable to find resource.';
  public readonly key = 'NotFound';


  constructor(public readonly errorDetails?: any) { }
}

export class Unauthorized {
  public readonly message = 'You do not have access to this resource.';
  public readonly key = 'Unauthorized';
}

export class ServerError {
  public readonly message =
    'An error has occurred while processing your request. We apologize for the inconvenience. If you need immediate assistance, please contact production support (866) 828-8074 or try your request later.';
  public readonly key = 'ServerError';

  constructor(public readonly errorDetails?: any) { }
}

export type AsyncError = Unauthorized | ServerError | Conflict | NotFound;
export type AsyncResult<TData> = E.Either<AsyncError, TData>;

export type AsyncOperation<TData> = Promise<AsyncResult<TData>>;

export const createLoading = <T>(value: T) => new Loading(value);
export const fromResult = <T>(value: T) => new Loaded(value);

export class Loaded<TData> {
  public readonly key = 'Loaded';
  public readonly Data: TData;

  constructor(data: TData) {
    this.Data = data;
  }
}

export class LoadingAdditional<TData> {
  public readonly key = 'LoadingAdditional';
  public readonly Data: TData;

  constructor(data: TData) {
    this.Data = data;
  }
}

export type Pending = Loading<any> | Empty;

export type Async<TData> = Loaded<TData> | Loading<TData> | Unauthorized | Conflict | ServerError | Empty | NotFound;

export const toEither = <TResponse>(async: Async<TResponse>): E.Either<Pending | AsyncError, TResponse> =>
  matchAsync(async, {
    Loaded: response => E.right(response),
    Loading: loading => E.left(loading),
    ServerError: error => E.left(error),
    Unauthorized: error => E.left(error),
    Conflict: error => E.left(error),
  });

export const toPageEither = <TResponse>(async: AsyncPage<TResponse>): E.Either<Pending | AsyncError, TResponse> =>
  matchAsyncPage(async, {
    Loaded: response => E.right(response),
    Loading: loading => E.left(loading),
    ServerError: error => E.left(error),
    Unauthorized: error => E.left(error),
    Conflict: error => E.left(error),
  });

export const map = <A, B>(transform: (a: A) => B) => (value: Async<A>) =>
  isLoaded(value) ? fromResult(transform(value.Data)) : value;

export const toPage = <T>(data: T[]): Page<T> => ({
  currentPageNumber: 0,
  links: [],
  result: data,
  totalCount: data.length,
});

type AsyncEither<TResponse> = E.Either<Pending | AsyncError, TResponse>;
export const toAsync = <T>(response: AsyncEither<T>): Async<T> =>
  pipe(
    response,
    E.fold(
      error => error,
      value => new Loaded(value) as any
    )
  );

export const isLoaded = <T>(async: Async<T> | AsyncPage<T>): async is Loaded<T> => async.key === 'Loaded';

export const isLoadedPage = <T>(async: AsyncPage<T>): async is Loaded<T> => async.key === 'Loaded';
export const isServerError = <T>(async: AsyncPage<T>): async is ServerError => async.key === 'ServerError';
export const isLoading = <T>(async: Async<T>): async is Loading<T> => ['Loading', 'Empty'].includes(async.key);

export type AsyncPage<TData> = Async<TData> | LoadingAdditional<TData>;

type AsyncOptions<TData, TReturn> = {
  Loading: (data: Loading<TData> | Empty) => TReturn;
  Loaded: (data: TData) => TReturn;
  ServerError: (error: ServerError | NotFound) => TReturn;
  Unauthorized: (error: Unauthorized) => TReturn;
  Conflict: (error: Conflict) => TReturn;
};

export const matchAsyncPage = <TData, TReturn>(asyncData: AsyncPage<TData>, options: AsyncOptions<TData, TReturn>) => {
  switch (asyncData.key) {
    case 'LoadingAdditional':
    case 'Loaded':
      return options.Loaded(asyncData.Data);
    case 'Empty':
    case 'Loading':
      return options.Loading(asyncData);
    case 'NotFound':
    case 'ServerError':
      return options.ServerError(asyncData);
    case 'Unauthorized':
      return options.Unauthorized(asyncData);
    case 'Conflict':
      return options.Conflict(asyncData);
  }
};

export const matchAsync = <TData, TReturn>(asyncData: Async<TData>, options: AsyncOptions<TData, TReturn>) => {
  switch (asyncData.key) {
    case 'Loaded':
      return options.Loaded(asyncData.Data);
    case 'Empty':
    case 'Loading':
      return options.Loading(asyncData);
    case 'Conflict':
      return options.Conflict(asyncData);
    case 'ServerError':
    case 'NotFound':
      return options.ServerError(asyncData);
    case 'Unauthorized':
      return options.Unauthorized(asyncData);
  }
};

export const getAsyncEq = <T>(dataEq: Eq<T>): Eq<Async<T>> => ({
  equals: (async1, async2) => (isLoaded(async1) && isLoaded(async2) ? dataEq.equals(async1.Data, async2.Data) : false),
});
